/**
 * The employee page's Fravær tab (owner brief, 2026-09-21): the vacation balance, then every
 * absence, newest first, filtered by the types this person actually has. The data has no
 * "vacation" or "sick" category, only type names and a sick flag, so the filters are the names.
 * A row opens the absence in the same drawer Fravær uses; "Nytt fravær" opens it empty.
 */
import type { ApiAbsence, ApiShift, ApiUserFull } from '@/api/data';
import { T } from '@/i18n';
import { oslo, today } from '@/rules';
import { Button, Chip, Empty, LoadError } from '@/ui';
import { fmtD, num, typeName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { useState } from 'react';
import { type AbsenceDraft, AbsenceDrawer, draftOfAbsence, newAbsenceDraft } from '../fravaer/AbsenceDrawer';
import type { Source } from './Oversikt';
import { upcomingAbsence } from './summary';

export function FravaerTab({
  u,
  abs,
  shifts,
  absQ,
}: {
  u: ApiUserFull;
  abs: ApiAbsence[];
  shifts: ApiShift[];
  absQ: Source;
}) {
  // an absence list that has not answered is unknown: «–» in the counts, never «Alle 0» (#272 B-03)
  const known = absQ.isSuccess;
  const [only, setOnly] = useState<string | null>(null);
  const [draft, setDraft] = useState<AbsenceDraft | null>(null);
  const names = new Map<string, number>();
  for (const a of abs) names.set(typeName(a.absence_type), (names.get(typeName(a.absence_type)) ?? 0) + 1);
  const list = abs.filter((a) => !only || typeName(a.absence_type) === only);
  const left = u.vacation_days_balance;
  const per = u.total_vacation_days_per_year;
  const ahead = upcomingAbsence(abs, today()).length;
  return (
    <>
      <div className="vtbar">
        <div className="tlfilters" style={{ margin: 0 }}>
          <button type="button" className={`chipf${only == null ? ' on' : ''}`} onClick={() => setOnly(null)}>
            {T('Alle')}
            <b>{known ? abs.length : '–'}</b>
          </button>
          {[...names].map(([n, c]) => (
            <button
              key={n}
              type="button"
              className={`chipf${only === n ? ' on' : ''}`}
              onClick={() => setOnly(n)}
            >
              {n}
              <b>{c}</b>
            </button>
          ))}
        </div>
        <span className="sp" />
        <Button onClick={() => setDraft(newAbsenceDraft(u.id, today()))}>
          <Icon name="plus" />
          {T('Nytt fravær')}
        </Button>
      </div>
      <div className="card2 ftop">
        <span className="ovi">
          <Icon name="sun" />
        </span>
        <span className="ftopt">
          <small>{T('Feriesaldo')}</small>
          <b>{left == null ? '–' : `${num(left)} ${T('dager igjen')}`}</b>
          {per ? <small>{T('{n} dager per år', { n: num(per) })}</small> : null}
        </span>
        {left != null && per ? (
          // days used, neutral: a full year left is an empty bar (#272 B-40)
          <span
            className="hbar ftbar used"
            title={T('{n} av {m} brukt', { n: num(Math.max(0, per - left)), m: num(per) })}
          >
            <i style={{ width: `${Math.max(0, Math.min(100, ((per - left) / per) * 100))}%` }} />
          </span>
        ) : (
          <span className="sp" />
        )}
        <small className="ovmute">
          {known ? ahead : '–'} {ahead === 1 && known ? T('kommende periode') : T('kommende perioder')}
        </small>
      </div>
      <section className="sec">
        <div className="card2">
          {absQ.isError ? (
            <LoadError what={T('fraværet')} onRetry={() => absQ.refetch()} />
          ) : !known ? (
            <Empty>{T('Henter…')}</Empty>
          ) : list.length ? (
            // on a phone a card per absence, every field labelled, nothing scrolled off the side (#272 B-07, B-S03)
            <div className="ftabwrap" data-testid="ftabwrap">
              <table className="t ftab">
                <thead>
                  <tr>
                    <th>{T('Periode')}</th>
                    <th>{T('Type')}</th>
                    <th>{T('Status')}</th>
                    <th>{T('Søkt')}</th>
                    <th>{T('Kommentar')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((a) => {
                    const from = oslo(a.start_date)?.date ?? '';
                    const to = oslo(a.end_date)?.date ?? '';
                    return (
                      <tr key={a.id}>
                        <td className="mono">
                          {/* the period opens the absence; a real button, so the keyboard reaches it */}
                          <button
                            type="button"
                            className="linkbtn"
                            onClick={() => setDraft(draftOfAbsence(a))}
                          >
                            {fmtD(from)}
                            {to !== from ? ` – ${fmtD(to)}` : ''}
                          </button>
                        </td>
                        <td data-k={T('Type')}>{typeName(a.absence_type)}</td>
                        <td className="fst">
                          {a.status === 'pending' ? (
                            <Chip tone="neu" small>
                              {T('Venter')}
                            </Chip>
                          ) : a.status === 'accepted' ? (
                            <Chip tone="ok" small>
                              {T('Innvilget')}
                            </Chip>
                          ) : (
                            <Chip tone="dgr" small>
                              {T('Avslått')}
                            </Chip>
                          )}
                        </td>
                        <td className="mono" data-k={T('Søkt')}>
                          {a.created_at ? fmtD(oslo(a.created_at)?.date ?? '') : '–'}
                        </td>
                        <td className="fcom" data-k={T('Kommentar')}>
                          {a.reason || '–'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty>{T('Ingen fravær registrert.')}</Empty>
          )}
        </div>
      </section>
      <AbsenceDrawer draft={draft} shifts={shifts} onClose={() => setDraft(null)} />
    </>
  );
}
